import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  Paper,
  IconButton,
  CircularProgress,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

/**
 * FaqUploadDialog 元件
 *
 * 上傳流程：
 * 1. 用戶選擇 Excel → 呼叫 API 上傳 → 伺服器快速插入 FAQ 記錄（< 2 秒）
 * 2. API 回傳 jobId + count + data → 通知父元件（FaqPage）開始背景追蹤
 * 3. 對話框顯示成功訊息後允許關閉
 *
 * ⚠️ Socket.IO 進度追蹤由 FaqPage.js 負責（避免重複連線和雙重回呼）
 */
const FaqUploadDialog = ({
  open,
  onClose,
  onUpload,
  onUploadSuccess
}) => {
  // 範本下載 URL
  const templateUrls = {
    type1: 'https://agent-rebaz-ai.s3.ap-northeast-1.amazonaws.com/template/AI%E5%9B%9E%E8%A6%86%E8%B3%87%E8%A8%8A_Excel%E7%AF%84%E6%9C%AC_type1_v1.0.1.xlsx',
    type2: 'https://agent-rebaz-ai.s3.ap-northeast-1.amazonaws.com/template/AI%E5%9B%9E%E8%A6%86%E8%B3%87%E8%A8%8A_Excel%E7%AF%84%E6%9C%AC_type2_v1.0.1.xlsx'
  };

  // 基本狀態
  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);

  // 上傳階段狀態：'idle' | 'inserting' | 'success' | 'error'
  const [uploadPhase, setUploadPhase] = useState('idle');

  // 插入結果（Phase 1 回傳）
  const [insertResult, setInsertResult] = useState(null);

  // 重置所有狀態
  const resetState = useCallback(() => {
    setFile(null);
    setError(null);
    setUploading(false);
    setUploadPhase('idle');
    setInsertResult(null);
  }, []);

  // 關閉時重置（只在非上傳中）
  useEffect(() => {
    if (!open && uploadPhase !== 'inserting') {
      resetState();
    }
  }, [open, uploadPhase, resetState]);

  // 檔案驗證
  const validateFile = (f) => {
    if (!f) return '請選擇檔案';

    const validTypes = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel.sheet.macroEnabled.12'
    ];
    const fileName = f.name.toLowerCase();
    const isExcelExtension = fileName.endsWith('.xls') || fileName.endsWith('.xlsx');

    if (!validTypes.includes(f.type) && !isExcelExtension) {
      return '請上傳 Excel 檔案 (.xls, .xlsx)';
    }

    if (f.size > 5 * 1024 * 1024) {
      return '檔案大小不得超過 5MB';
    }

    return null;
  };

  // 檔案選擇
  const handleFileChange = (event) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    const validationError = validateFile(selectedFile);
    if (validationError) {
      setError(validationError);
      return;
    }

    setFile(selectedFile);
    setError(null);
  };

  // 拖放處理
  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };
  const handleDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files?.length > 0) {
      const droppedFile = e.dataTransfer.files[0];
      const validationError = validateFile(droppedFile);
      if (validationError) {
        setError(validationError);
        return;
      }
      setFile(droppedFile);
      setError(null);
    }
  };

  // 上傳處理
  const handleUpload = async () => {
    if (!file) {
      setError('請選擇檔案');
      return;
    }

    try {
      setUploading(true);
      setUploadPhase('inserting');
      setError(null);

      // 呼叫上傳 API（快速回應：解析 Excel → 批量插入 → 回傳 jobId）
      const result = await onUpload(file);

      setInsertResult(result);
      setUploadPhase('success');

      // 通知父元件（FaqPage）— 只呼叫一次
      // FaqPage 會：
      //   1. 設置 activeJobId → 觸發 Socket.IO 監聽
      //   2. 設置 processingFaqIds → 顯示 "處理中" 標記
      //   3. 重新整理 FAQ 列表
      if (onUploadSuccess) {
        onUploadSuccess(result);
      }

      // 短暫延遲後自動關閉對話框
      setTimeout(() => {
        if (open) {
          onClose();
        }
      }, 2000);

    } catch (err) {
      setError(err.message || '上傳失敗');
      setUploadPhase('error');
      setUploading(false);
    }
  };

  // 移除選擇的檔案
  const handleRemoveFile = () => {
    setFile(null);
    setError(null);
  };

  // 範本下載
  const handleDownloadTemplate = (templateType) => {
    const url = templateUrls[templateType];
    if (url) {
      const link = document.createElement('a');
      link.href = url;
      link.download = templateType === 'type1'
        ? 'AI回覆資訊_Excel範本_type1_v1.0.1.xlsx'
        : 'AI回覆資訊_Excel範本_type2_v1.0.1.xlsx';
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // === 渲染 ===
  return (
    <Dialog
      open={open}
      onClose={uploadPhase === 'idle' || uploadPhase === 'error' ? onClose : undefined}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
      disableEscapeKeyDown={uploading}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1
        }}
      >
        <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
          上傳 FAQ Excel 檔案
        </Typography>
        {(uploadPhase === 'idle' || uploadPhase === 'error') && (
          <IconButton aria-label="關閉" onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent>
        {/* === Phase: 插入中 (< 2 秒) === */}
        {uploadPhase === 'inserting' && (
          <Box sx={{ mt: 2, mb: 4, textAlign: 'center' }}>
            <CircularProgress size={40} sx={{ mb: 2 }} />
            <Typography variant="body1" gutterBottom>
              正在解析檔案並建立 FAQ 記錄...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              這通常只需要幾秒鐘
            </Typography>
          </Box>
        )}

        {/* === Phase: 成功（FAQ 已插入，背景處理開始） === */}
        {uploadPhase === 'success' && (
          <Box sx={{ mt: 2, mb: 2, textAlign: 'center' }}>
            <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
            <Typography variant="h6" color="success.main" gutterBottom>
              上傳成功！
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              已建立 {insertResult?.count || 0} 筆 FAQ 記錄
            </Typography>
            {insertResult?.jobId && (
              <Alert severity="info" sx={{ mt: 2, textAlign: 'left' }}>
                AI 正在背景為每筆 FAQ 生成嵌入向量和改寫問句。
                您可以繼續使用系統，處理進度會顯示在 FAQ 列表中。
              </Alert>
            )}
          </Box>
        )}

        {/* === Phase: 空閒（選擇檔案） === */}
        {(uploadPhase === 'idle' || uploadPhase === 'error') && (
          <>
            {/* 拖放上傳區域 */}
            <Box
              component="label"
              htmlFor="faq-file-upload"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px dashed #ccc',
                borderRadius: 2,
                p: 4,
                my: 2,
                cursor: 'pointer',
                transition: 'all 0.3s',
                backgroundColor: '#f9f9f9',
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                  borderColor: '#aaa'
                }
              }}
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <input
                type="file"
                id="faq-file-upload"
                accept=".xls,.xlsx"
                onChange={handleFileChange}
                style={{ display: 'none' }}
                disabled={uploading}
              />

              {file ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DescriptionIcon color="primary" />
                  <Typography>{file.name}</Typography>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.preventDefault();
                      handleRemoveFile();
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <>
                  <CloudUploadIcon sx={{ fontSize: 48, color: '#999', mb: 1 }} />
                  <Typography variant="body1" fontWeight="bold">
                    點擊或拖曳檔案至此處
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    支援 Excel 檔案格式 (.xls, .xlsx)
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    檔案大小不得超過 5MB
                  </Typography>
                </>
              )}
            </Box>

            {/* 錯誤訊息 */}
            {error && (
              <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                {error}
              </Alert>
            )}

            {/* 範本下載 */}
            <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                ℹ️ 請確保上傳的 Excel 欄位符合規定格式。選擇適合的範本：
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button
                  startIcon={<DownloadIcon />}
                  onClick={() => handleDownloadTemplate('type1')}
                  size="small"
                  sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                >
                  單純問答上傳範本
                </Button>
                <Button
                  startIcon={<DownloadIcon />}
                  onClick={() => handleDownloadTemplate('type2')}
                  size="small"
                  sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                >
                  預設關鍵字問答上傳範本
                </Button>
              </Box>
            </Paper>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        {/* 空閒/錯誤狀態：取消 + 上傳 */}
        {(uploadPhase === 'idle' || uploadPhase === 'error') && (
          <>
            <Button variant="outlined" onClick={onClose}>
              取消
            </Button>
            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={!file || uploading}
              startIcon={<CloudUploadIcon />}
            >
              上傳
            </Button>
          </>
        )}

        {/* 成功狀態：等待自動關閉，或手動關閉 */}
        {uploadPhase === 'success' && (
          <Button
            variant="contained"
            color="success"
            onClick={() => {
              onClose();
            }}
          >
            完成
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default FaqUploadDialog;